// ============================================================================
// PsyMethodsCatalog — ИНТЕРФЕЙС ПСИХОЛОГИЧЕСКИХ МЕТОДИК (рабочее место психолога).
//
// Один экран без таблиц и модальных окон для результатов:
//   • список методик с ПОИСКОМ и КАТЕГОРИЯМИ (PollMethodsCatalog);
//   • запуск прохождения — существующий раннер FillRunner2, протокол пишется
//     серверным PollService.submitPollAnswers (subject_id = пациент);
//   • результаты и история прохождения — встроенная панель методики и общая
//     история (PsyResultsHistory), новые сверху;
//   • явные состояния: загрузка, ошибка (+ «Повторить»), пусто, нет результатов,
//     нет прав на протоколы, частичный сбой чтения шаблонов.
//
// SSoT — сервер: банк методик, назначения и протоколы читаются существующими
// RPC (lib/psyWorkspace + каталог методик). Новых серверных механизмов нет.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FlaskConical, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToastStore } from '@/store/toastStore';
import { getSDK } from '@/store/sdkStore';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import {
  activeLink, assignmentFor, loadAssignments, loadPatientResultsDetailed,
  type PsyAssignment, type PsyMethod, type PatientResult,
} from '@/lib/psyWorkspace';
import {
  loadPollMethodCatalog, type PollMethodCatalog, type PollMethodItem,
} from '@/components/polls/pollMethods';
import { PollMethodsCatalog } from '@/components/polls/PollMethodsCatalog';
import { FillRunner as FillModal } from '@/components/polls/FillRunner2';
import { PollManualProtocolDialog } from '@/components/polls/PollManualProtocolDialog';
import { PsyAssignDialog } from '@/components/psy/PsyAssignDialog';
import { PsyMethodDetails } from '@/components/psy/PsyMethodDetails';
import { PsyResultsHistory } from '@/components/psy/PsyResultsHistory';

export interface PsyMethodsCatalogProps {
  patientId: string;
  patientName: string;
  canManage?: boolean;
}

/** Методика в форме, которую ожидают существующие диалоги назначения/результата. */
function toPsyMethod(method: PollMethodItem): PsyMethod {
  return {
    code: method.code,
    name: method.name,
    synonym: method.synonym,
    description: method.description,
    scoring: method.scoring,
    status: method.status,
    templateId: method.templateId,
  };
}

export function PsyMethodsCatalog({ patientId, patientName, canManage = false }: PsyMethodsCatalogProps) {
  const showError = useToastStore((s) => s.showError);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [catalog, setCatalog] = useState<PollMethodCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [results, setResults] = useState<PatientResult[]>([]);
  const [assignments, setAssignments] = useState<PsyAssignment[]>([]);
  const [resultsDenied, setResultsDenied] = useState(false);
  const [resultsError, setResultsError] = useState('');
  const [detailsCode, setDetailsCode] = useState('');
  const [fill, setFill] = useState<{ id: string; name: string } | null>(null);
  const [fillValues, setFillValues] = useState<Record<string, unknown>>({});
  const [manual, setManual] = useState<PollMethodItem | null>(null);
  const [assign, setAssign] = useState<PollMethodItem | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (force = false) => {
    setLoading(true);
    setError('');
    try {
      const nextCatalog = await loadPollMethodCatalog({ force });
      setCatalog(nextCatalog);
    } catch (e) {
      logErrorToJournal(e, 'Психолог: каталог методик');
      setError(friendlyError(e));
      setLoading(false);
      return;
    }
    // Протоколы/назначения — отдельный блок: их сбой не скрывает методики.
    setResultsError('');
    try {
      const [nextAssignments, nextResults] = await Promise.all([
        loadAssignments(patientId),
        loadPatientResultsDetailed(patientId),
      ]);
      setAssignments(nextAssignments);
      setResults(nextResults.items);
      setResultsDenied(nextResults.denied);
    } catch (e) {
      logErrorToJournal(e, `Психолог: результаты методик пациента ${patientId}`);
      setResultsError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  // Первичная загрузка — асинхронно после монтирования (не setState в теле эффекта).
  useEffect(() => {
    const timer = setTimeout(() => { void load(); }, 0);
    return () => clearTimeout(timer);
  }, [load]);

  const items = useMemo(() => catalog?.items || [], [catalog]);
  const methodByCode = useMemo(() => new Map(items.map((m) => [m.code, m])), [items]);
  const details = detailsCode ? methodByCode.get(detailsCode) || null : null;

  const resultsByCode = useMemo(() => {
    const map = new Map<string, PatientResult[]>();
    for (const r of results) {
      const list = map.get(r.methodCode);
      if (list) list.push(r);
      else map.set(r.methodCode, [r]);
    }
    for (const list of map.values()) {
      list.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
    }
    return map;
  }, [results]);

  const methodName = useCallback(
    (code: string) => methodByCode.get(code)?.synonym || code,
    [methodByCode],
  );

  const startFill = (method: PollMethodItem) => {
    if (!patientId) { showError('Не выбран пациент — прохождение не запущено'); return; }
    if (!method.templateId) { showError('У методики нет шаблона вопросов'); return; }
    setFillValues({});
    setFill({ id: method.templateId, name: method.synonym });
  };

  const submitFill = async () => {
    if (!fill) return;
    setBusy(true);
    try {
      const answers = Object.entries(fillValues).map(([questionId, value]) => ({
        questionId,
        valueJson: JSON.stringify(value ?? ''),
      }));
      await getSDK().poll.submitPollAnswers({
        pollId: fill.id,
        subjectId: patientId,
        attempt: 0,
        answers,
      });
      useToastStore.getState().showSuccess('Результат сохранён');
      setFill(null);
      setFillValues({});
      await load();
    } catch (e) {
      showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const renderBadges = (method: PollMethodItem) => {
    const assignment = assignmentFor(assignments, method.code);
    const last = resultsByCode.get(method.code)?.[0];
    return (
      <>
        {last && (
          <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-400">
            есть результат{last.score ? ` · балл: ${last.score}` : ''}
          </span>
        )}
        {!last && assignment && (
          <span className="rounded-full border border-primary/25 bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">
            {String(assignment.status || '').toLowerCase() === 'assigned'
              ? (activeLink(assignment) ? 'назначено · есть код' : 'назначено')
              : 'назначение завершено'}
          </span>
        )}
      </>
    );
  };

  const renderActions = (method: PollMethodItem) => {
    const assignment = assignmentFor(assignments, method.code);
    return (
      <>
        {!method.manualOnly && (
          <Button size="sm" className="h-7 px-2 text-[11px]" onClick={() => startFill(method)}>
            Пройти
          </Button>
        )}
        {method.manualOnly && canManage && (
          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
            onClick={() => setManual(method)}>
            Результат вручную
          </Button>
        )}
        {canManage && !method.manualOnly && (
          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => setAssign(method)}>
            {activeLink(assignment) ? 'Ссылка и код' : 'Назначить'}
          </Button>
        )}
      </>
    );
  };

  const retry = () => { void load(true); };

  // Выбранная методика — панель в начале списка: прокручиваем к ней (мобильно).
  useEffect(() => {
    if (detailsCode) scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [detailsCode]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2">
        <FlaskConical className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Методики</span>
        {catalog && (
          <span className="text-[11px] text-muted-foreground">
            методик: {items.length} · результатов: {results.length}
          </span>
        )}
        {loading && <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto">
        {details && (
          <div className="px-3 pt-3">
            <PsyMethodDetails
              method={details}
              assignment={assignmentFor(assignments, details.code)}
              results={resultsByCode.get(details.code) || []}
              canManage={canManage}
              patientName={patientName}
              onClose={() => setDetailsCode('')}
              onFill={() => startFill(details)}
              onManual={() => setManual(details)}
              onAssign={() => setAssign(details)}
            />
          </div>
        )}

        <PollMethodsCatalog
          items={items}
          loading={loading}
          error={error}
          warnings={catalog?.warnings}
          onRetry={retry}
          onOpen={(m) => setDetailsCode(m.code === detailsCode ? '' : m.code)}
          renderBadges={renderBadges}
          renderActions={renderActions}
          selectedCode={detailsCode}
          emptyTitle="Методик нет"
          emptyText={'В базе нет типов опросов. Установите поставку с методиками (например, «ГидраМедик: психология»).'}
        />

        <div className="px-3 pb-4">
          {resultsError ? (
            <div className="space-y-1.5 rounded-lg border border-destructive/40 px-3 py-2 text-xs">
              <div className="text-sm font-medium text-destructive">Не удалось загрузить результаты</div>
              <div className="text-muted-foreground break-words">{resultsError}</div>
              <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={retry}>
                Повторить
              </Button>
            </div>
          ) : (
            <PsyResultsHistory
              results={results}
              assignments={assignments}
              methodName={methodName}
              denied={resultsDenied}
              emptyText="Результатов пока нет — выберите методику и пройдите её вместе с пациентом."
            />
          )}
        </div>
      </div>

      {fill && (
        <FillModal
          poll={fill}
          values={fillValues}
          setValues={setFillValues}
          busy={busy}
          subjectName={patientName}
          onCancel={() => { setFill(null); setFillValues({}); }}
          onSubmit={() => void submitFill()}
        />
      )}

      {manual && (
        <PollManualProtocolDialog
          pollId={manual.templateId}
          pollName={manual.synonym}
          psyMode
          subject={{ id: patientId, name: patientName }}
          onClose={() => setManual(null)}
          onSaved={() => void load()}
        />
      )}

      {assign && (
        <PsyAssignDialog
          patientId={patientId}
          patientName={patientName}
          method={toPsyMethod(assign)}
          assignment={assignmentFor(assignments, assign.code)}
          onClose={() => { setAssign(null); void load(); }}
          onManual={() => { setManual(assign); setAssign(null); }}
        />
      )}
    </div>
  );
}
